import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import { BigNumber as BigNumber_ } from '@blade/std-lib'
import { Address } from '@root/entities/address/Address'
import { Provider } from '@root/entities/provider'

import { PairAddress } from './PairAddress'

import type {
  PoolBondingCurve,
  PoolToMigration,
  PoolFromMigration,
} from '@blade/blockchain-types'
import type { TypeHelper } from '@blade/std-lib'
import type { ServicesApi } from '@root/shared/api'

type PoolToMigrationDto = TypeHelper.OverrideKeys<
  PoolToMigration,
  { migratedToProvider: Provider.Id }
>

type PoolFromMigrationDto = TypeHelper.OverrideKeys<
  PoolFromMigration,
  { migratedFromProvider: Provider.Id }
>

export type GraduationQuoteCurrency = 'sol' | 'usdc'

export interface BondingCurve {
  curveType: string
  bondingPercentage: number
  graduationPrice: BigNumber
  graduationMcap: BigNumber
  // Unit of graduationMcap/Price:
  //   'sol'  → multiply by current SOL/USD price for USD display
  //   'usdc' → already in USD-equivalent, render as-is
  // Optional for backward compat with old DB records; missing means 'sol'.
  graduationQuoteCurrency: GraduationQuoteCurrency
}

export interface PairToMigration {
  migratedToPairAddress: PairAddress
  migratedToProvider: Provider.Provider
  migrationTimestamp: Date
  migrationPrice: BigNumber
  migrationMarketCap: BigNumber
  migrationTxHash: string
}

export interface PairFromMigration {
  migrationTimestamp: Date
  migrationPrice: BigNumber
  migrationMarketCap: BigNumber
  migrationTxHash: string
  migratedFromPairAddress: PairAddress
  migratedFromProvider: Provider.Provider
}

interface PumpfunPairExtraDetails {
  provider: typeof Provider.pumpFun.id
  migration: PairToMigration | undefined
  bondingCurve: BondingCurve | undefined
  isCashbackMode: boolean
  buybackFeeBasisPoints: BigNumber | undefined
}

interface PumpSwapPairExtraDetails {
  provider: typeof Provider.pumpSwap.id
  migration: PairFromMigration | undefined
  isCashbackMode: boolean
  cashback: BigNumber | undefined
  cashbackFeeBasisPoints: number | undefined
  coinCreator: Address | undefined
  coinCreatorFee: BigNumber | undefined
  coinCreatorFeeBasisPoints: number | undefined
  buybackFee: BigNumber | undefined
  buybackFeeBasisPoints: BigNumber | undefined
}

interface RaydiumCpmmPairExtraDetails {
  provider: typeof Provider.raydiumCpmm.id
  migration: PairFromMigration | undefined
}

interface RaydiumV4PairExtraDetails {
  provider: typeof Provider.raydiumV4.id
  migration: PairFromMigration | undefined
}

interface RaydiumLaunchpadPairExtraDetails {
  provider: typeof Provider.raydiumLaunchpad.id
  bondingCurve: BondingCurve | undefined
  migration: PairToMigration | undefined
}

interface BonkFunPairExtraDetails {
  provider: typeof Provider.bonkFun.id
  bondingCurve: BondingCurve | undefined
  migration: PairToMigration | undefined
}

export type PairExtraDetails =
  | PumpfunPairExtraDetails
  | PumpSwapPairExtraDetails
  | RaydiumCpmmPairExtraDetails
  | RaydiumV4PairExtraDetails
  | RaydiumLaunchpadPairExtraDetails
  | BonkFunPairExtraDetails

export const fromDto = (dto: ServicesApi.PairExtraDetailsDto) => {
  return match(dto)
    .returnType<PairExtraDetails>()
    .with({ provider: 'pump-fun' }, (dto) => {
      return {
        provider: Provider.pumpFun.id,
        bondingCurve: dto.bondingCurve
          ? bondingCurveFromDto(dto.bondingCurve)
          : undefined,
        migration: dto.migration
          ? migrationToPairFromDto(dto.migration)
          : undefined,
        buybackFeeBasisPoints: dto.buybackFeeBasisPoints
          ? BigNumber_.make(dto.buybackFeeBasisPoints).valueOrUndefined
          : undefined,
        isCashbackMode: dto.isCashbackMode ?? false,
      }
    })
    .with({ provider: 'pump-swap' }, (dto): PumpSwapPairExtraDetails => {
      return {
        provider: Provider.pumpSwap.id,
        migration: dto.migration
          ? migrationFromPairFromDto(dto.migration)
          : undefined,
        isCashbackMode: dto.isCashbackMode ?? false,
        cashback: dto.cashback ? BigNumber(dto.cashback) : undefined,
        cashbackFeeBasisPoints: dto.cashbackFeeBasisPoints
          ? parseFloat(dto.cashbackFeeBasisPoints)
          : undefined,
        coinCreator: dto.coinCreator ? Address(dto.coinCreator) : undefined,
        coinCreatorFee: dto.coinCreatorFee
          ? BigNumber(dto.coinCreatorFee)
          : undefined,
        coinCreatorFeeBasisPoints: dto.coinCreatorFeeBasisPoints
          ? parseFloat(dto.coinCreatorFeeBasisPoints)
          : undefined,
        buybackFee: dto.buybackFee ? BigNumber(dto.buybackFee) : undefined,
        buybackFeeBasisPoints: dto.buybackFeeBasisPoints
          ? BigNumber_.make(dto.buybackFeeBasisPoints).valueOrUndefined
          : undefined,
      }
    })
    .with({ provider: 'raydium-cpmm' }, (dto) => {
      return {
        provider: Provider.raydiumCpmm.id,
        migration: dto.migration
          ? migrationFromPairFromDto(dto.migration)
          : undefined,
      }
    })
    .with({ provider: 'raydium-v4' }, (dto) => {
      return {
        provider: Provider.raydiumV4.id,
        migration: dto.migration
          ? migrationFromPairFromDto(dto.migration)
          : undefined,
      }
    })
    .with({ provider: 'raydium-launchpad' }, (dto) => {
      return {
        provider: Provider.raydiumLaunchpad.id,
        bondingCurve: dto.bondingCurve
          ? bondingCurveFromDto(dto.bondingCurve)
          : undefined,
        migration: dto.migration
          ? migrationToPairFromDto(dto.migration)
          : undefined,
      }
    })
    .with({ provider: 'bonk-fun' }, (dto) => {
      return {
        provider: Provider.bonkFun.id,
        bondingCurve: dto.bondingCurve
          ? bondingCurveFromDto(dto.bondingCurve)
          : undefined,
        migration: dto.migration
          ? migrationToPairFromDto(dto.migration)
          : undefined,
      }
    })
    .exhaustive()
}

const bondingCurveFromDto = (dto: PoolBondingCurve): BondingCurve => {
  return {
    curveType: dto.curveType,
    bondingPercentage: parseFloat(dto.bondingPercentage),
    graduationPrice: BigNumber(dto.graduationPrice),
    graduationMcap: BigNumber(dto.graduationMcap),
    graduationQuoteCurrency: dto.graduationQuoteCurrency ?? 'sol',
  }
}

const migrationBaseFromDto = (dto: PoolFromMigrationDto | PoolToMigrationDto) =>
  ({
    migrationTimestamp: new Date(dto.migrationTimestamp),
    migrationMarketCap: BigNumber(dto.migrationMarketCap),
    migrationPrice: BigNumber(dto.migrationPrice),
    migrationTxHash: dto.migrationTxHash,
  }) satisfies Partial<PairToMigration | PairFromMigration>

const migrationToPairFromDto = (dto: PoolToMigrationDto): PairToMigration => {
  return {
    ...migrationBaseFromDto(dto),
    migratedToPairAddress: PairAddress(dto.migratedToPoolAddress),
    migratedToProvider: Provider.byId[dto.migratedToProvider],
  }
}

const migrationFromPairFromDto = (
  dto: PoolFromMigrationDto,
): PairFromMigration => {
  return {
    ...migrationBaseFromDto(dto),
    migratedFromPairAddress: PairAddress(dto.migratedFromPoolAddress),
    migratedFromProvider: Provider.byId[dto.migratedFromProvider],
  }
}

export const isPumpFunMayhem = (
  extraDetails: ServicesApi.PairExtraDetailsDto,
) => {
  return Boolean(
    extraDetails.provider === 'pump-fun' && extraDetails.isMayhemMode,
  )
}
